import React, { Fragment, Suspense, lazy } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { Link } from 'react-router-dom';
import { Container, Row, Col, Card, Button } from 'react-bootstrap';
import Icon from '../../../../../components/Content/Icon.js';

import { injectIntl } from '../../../../../components/Intl/withIntl.js';

import { Helmet } from '../../../../../components/Content/Helmet.js';
import config from '../../../../../config.js';
import { getFile } from '../../../../../util/file.js';
import { dateFormat } from '../../../../../util/date.js';

import { getContentBlockItemsByName, getContentBlockItemByName, getContentBlockMixedItem, getContentBlockItemValue } from '../../../CmsActions.js';
import { getWebSiteRef } from '../../../../App/AppSchema.js';

import HtmlComponent from '../../../../../components/Text/HtmlComponent.js';
import CmsBlockItem from '../../content/CmsBlockItem.js';
import CmsPanel from '../../content/CmsPanel.js';
import PredictiveLink from '../../view/PredictiveLink.js';
import PageCover from '../../view/PageCover.js';
import PreFooter from '../../../../App/components/PreFooter.js';

function PageDefault({ intl, page }) {
    const contents = getContentBlockItemsByName(page.blocks, 'content');
    const promotion = getContentBlockItemByName(page.blocks, 'promotion');
    // Raw JSON-LD entered through the CMS interface, injected as-is in the <head>.
    // Tolerate content pasted with or without the surrounding <script> wrapper (same as PostDefault).
    const schema = getContentBlockItemValue(page.blocks, 'schema').replace(/<\/?script[^>]*>/gi, '').trim();

    // Minimal WebPage node carrying the page's own dates, so `dates.updated` surfaces as dateModified
    // (nothing else does it for pages). Keyed with @id `${url}#webpage`: a CollectionPage/WebPage the
    // editor pastes in the `schema` field with the same @id merges with this node instead of duplicating.
    // datePublished comes from dates.created; dateModified from dates.updated, dropped when it is not
    // strictly after publication (an equal date carries no information), mirroring PostDefault.
    const pageUrl = `${config.url}${page.language || intl.locale}/${page.slug}`;
    const publishedDay = (page.dates || {}).created ? dateFormat((page.dates || {}).created, 'YYYY-MM-DD') : '';
    const modifiedDay = (page.dates || {}).updated ? dateFormat((page.dates || {}).updated, 'YYYY-MM-DD') : '';
    const hasModifiedDate = Boolean(modifiedDay) && (!publishedDay || modifiedDay > publishedDay);
    const webPageLd = JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        '@id': `${pageUrl}#webpage`,
        url: pageUrl,
        inLanguage: page.language || intl.locale,
        isPartOf: getWebSiteRef(),
        ...(publishedDay ? { datePublished: publishedDay } : {}),
        ...(hasModifiedDate ? { dateModified: modifiedDay } : {}),
    });

    const renderContent = (content, isFullColumn = false, index = 0) => {
        const text = getContentBlockMixedItem(content, 'text') && <HtmlComponent text={getContentBlockMixedItem(content, 'text').text} />;
        const image = getContentBlockMixedItem(content, 'image') && getContentBlockMixedItem(content, 'image').path && <CmsBlockItem item={getContentBlockMixedItem(content, 'image')} type="image" className="mt-3 mt-md-0 w-100" />;
        const cta = getContentBlockMixedItem(content, 'call-to-action') && getContentBlockMixedItem(content, 'call-to-action').text && <div className="text-center"><Button variant={getContentBlockMixedItem(content, 'call-to-action').color || 'warning'} size="lg" as={PredictiveLink} to={getContentBlockMixedItem(content, 'call-to-action').path} className="mt-3">{getContentBlockMixedItem(content, 'call-to-action').text}</Button></div>;
        const panel = getContentBlockMixedItem(content, 'panel') && getContentBlockMixedItem(content, 'panel').text && <div className="my-4"><CmsPanel slug={getContentBlockMixedItem(content, 'panel').text} /></div>;
        const position = getContentBlockMixedItem(content, 'position') && getContentBlockMixedItem(content, 'position').text;
        return (
            <div key={index} className={classnames('mb-5', !isFullColumn && 'critical-css-limiter')}>
                {
                    isFullColumn
                    ? (
                        <Fragment>
                            {image}
                            {text}
                            {cta}
                            {panel}
                        </Fragment>
                    ) : (
                        <Fragment>
                            <Row>
                                {
                                    (!position || position === 'right')
                                    ? (
                                        <Fragment>
                                            <Col xs="12" md={image ? 8 : 12}>
                                                {text}
                                            </Col>
                                            {image && <Col>{image}</Col>}
                                        </Fragment>
                                    ) : (
                                        <Fragment>
                                            {image && <Col>{image}</Col>}
                                            <Col xs="12" md={image ? 8 : 12}>
                                                {text}
                                            </Col>
                                        </Fragment>
                                    )
                                }
                            </Row>
                            {cta}
                            {panel}
                        </Fragment>
                    )
                }
            </div>
        );
    };

    return (
        <Fragment>
            <Helmet>
                <script type="application/ld+json">{webPageLd}</script>
                {/* Ternaire (et non `&&`) : un `schema` vide rendrait la chaine '' comme enfant direct de <Helmet>, qui n'accepte que des elements. */}
                {schema ? <script type="application/ld+json">{schema}</script> : null}
            </Helmet>

            <PageCover page={page} coverKey="image" />

            <Container fluid="xl" className={classnames('mt-lg-n5')}>
                <Card className={classnames('px-md-2', 'p-lg-5')}>
                    <Card.Body>
                        {contents && contents.length && contents.map((content, index) => {
                            if(index === 0 && promotion) {
                                return (
                                    <Row key={index} className="my-3">
                                        <Col xs={{ span: 12, order: 2 }} md={{ span: 7, order: 1 }}>
                                            {renderContent(content, true, index)}
                                        </Col>
                                        <Col xs={{ span: 12, order: 1 }} md={{ span: 5, order: 2 }} className="mb-3">
                                            <div className="d-none d-md-block bg-tertiary" style={{ paddingTop: '120px' }}>
                                                {getContentBlockMixedItem(promotion, 'image') && (
                                                    <div className="d-none d-md-block position-absolute w-100 text-center" style={{ top: '-300px', left: 0 }}>
                                                        <CmsBlockItem item={getContentBlockMixedItem(promotion, 'image')} type="image" style={{ maxHeight: '450px' }} />
                                                    </div>
                                                )}
                                                <div className="p-3">
                                                    {getContentBlockMixedItem(promotion, 'title') && <span className="h6 font-weight-bold">{getContentBlockMixedItem(promotion, 'title').text}</span>}
                                                    {getContentBlockMixedItem(promotion, 'call-to-action') && getContentBlockMixedItem(promotion, 'call-to-action').path && (
                                                        <Button variant="secondary" as={PredictiveLink} to={getContentBlockMixedItem(promotion, 'call-to-action').path} className="d-block mt-3">{getContentBlockMixedItem(promotion, 'call-to-action').text}</Button>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="d-block d-md-none p-3 bg-tertiary">
                                                <div className="text-center"><CmsBlockItem item={getContentBlockMixedItem(promotion, 'image')} type="image" className="mb-3 w-75" /></div>
                                                {getContentBlockMixedItem(promotion, 'title') && <span className="h6 font-weight-bold">{getContentBlockMixedItem(promotion, 'title').text}</span>}
                                                {getContentBlockMixedItem(promotion, 'call-to-action') && getContentBlockMixedItem(promotion, 'call-to-action').path && getContentBlockMixedItem(promotion, 'call-to-action').text && (
                                                    <Button variant="secondary" as={PredictiveLink} to={getContentBlockMixedItem(promotion, 'call-to-action').path} className="d-block mt-3">{getContentBlockMixedItem(promotion, 'call-to-action').text}</Button>
                                                )}
                                            </div>
                                        </Col>
                                    </Row>
                                );
                            }
                            return renderContent(content, false, index);
                        })}
                    </Card.Body>
                </Card>
            </Container>
            <PreFooter />
        </Fragment>
    );
}

PageDefault.propTypes = {
    intl: PropTypes.object.isRequired,
    page: PropTypes.object.isRequired,
};

export default injectIntl(PageDefault);
